import { NavLink } from "react-router-dom";
import { motion } from "framer-motion";
import { ChevronLeft } from "lucide-react";
import { NAV_ITEMS } from "@/data/nav";
import { useUser } from "@/context/UserContext";
import { ReviewBadge } from "@/components/product/ReviewBadge";
import { FlagBadge } from "@/components/layout/FlagBadge";
import { cn } from "@/lib/utils";

const LOGO =
  "https://customer-assets-v7afamib.emergentagent.net/job_edju-command/artifacts/tmtoc27k_edju-cat-type-logo-png.png";

export const Sidebar = ({ collapsed, onToggle }) => {
  const { isAdmin, can } = useUser();
  const items = NAV_ITEMS.filter(
    (i) => (!i.adminOnly || isAdmin) && (!i.requires || can(i.requires))
  );
  const businessItems = items.filter((i) => !i.adminOnly);
  const adminItems = items.filter((i) => i.adminOnly);

  const renderItem = (item) => {
    const Icon = item.icon;
    return (
      <NavLink
        key={item.to}
        to={item.to}
        end={item.to === "/"}
        data-testid={item.testid}
        title={collapsed ? item.label : undefined}
        className={({ isActive }) =>
          cn(
            "group relative flex items-center gap-3 rounded-2xl px-3 py-2.5 text-sm font-semibold transition-colors duration-200",
            collapsed && "justify-center",
            isActive
              ? "bg-white/10 text-white"
              : "text-[#AEBEE8] hover:bg-white/[0.06] hover:text-white"
          )
        }
      >
        {({ isActive }) => (
          <>
            {isActive && (
              <motion.span
                layoutId="sidebar-active"
                className="absolute left-0 top-1/2 h-7 -translate-y-1/2 rounded-r-full"
                style={{ width: 4, backgroundColor: "var(--accent-secondary)" }}
              />
            )}
            <Icon
              size={20}
              strokeWidth={2}
              className="shrink-0"
              style={{ color: isActive ? "var(--accent-secondary)" : "#8FA3D8" }}
            />
            {!collapsed && <span className="truncate">{item.label}</span>}
            {item.to === "/product" && <ReviewBadge collapsed={collapsed} className={collapsed ? undefined : "ml-auto"} />}
            {item.to === "/" && <FlagBadge collapsed={collapsed} className={collapsed ? undefined : "ml-auto"} />}
          </>
        )}
      </NavLink>
    );
  };

  return (
    <aside
      data-testid="app-sidebar"
      className={cn(
        "relative z-40 hidden shrink-0 flex-col bg-[#14245F] text-white transition-[width] duration-300 ease-in-out md:flex",
        collapsed ? "w-20" : "w-64"
      )}
    >
      {/* Brand */}
      <div className={cn("flex h-16 items-center border-b border-white/10", collapsed ? "justify-center px-2" : "px-5")}>
        {collapsed ? (
          <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-[#67E8F9]/15 font-heading text-sm font-bold text-[#67E8F9]">
            E
          </div>
        ) : (
          <img src={LOGO} alt="Edju Cat" className="h-7 w-auto" />
        )}
      </div>

      {/* Nav */}
      <nav className="edju-scroll flex-1 space-y-1.5 overflow-y-auto px-3 py-5">
        {businessItems.map(renderItem)}

        {adminItems.length > 0 && (
          <div className="pt-5">
            {!collapsed ? (
              <p className="px-3 pb-2 text-[10px] font-bold uppercase tracking-[0.2em] text-[#6E82BC]">
                Administration
              </p>
            ) : (
              <div className="mx-3 mb-2 border-t border-white/10" />
            )}
            <div className="space-y-1.5">{adminItems.map(renderItem)}</div>
          </div>
        )}
      </nav>

      {/* Collapse toggle */}
      <button
        onClick={onToggle}
        data-testid="sidebar-collapse-toggle"
        className="flex h-12 items-center gap-2 border-t border-white/10 px-5 text-xs font-semibold text-[#8FA3D8] transition-colors hover:text-white"
      >
        <ChevronLeft
          size={18}
          strokeWidth={2}
          className={cn("transition-transform duration-300", collapsed && "rotate-180")}
        />
        {!collapsed && <span>Collapse</span>}
      </button>
    </aside>
  );
};
